import {
    Box,
    Button,
    Collapse,
    Divider,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useGetFeeListQuery } from '../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../shared/AuthRoles';
import {
    camelCaseToSentenceCase,
    camelCaseToTitleCase,
    capitalizeName,
    formatTime,
} from '../../../shared/utils';
import { ExperienceType, FeeType } from '../../enums/experience';
import { ExperienceDiscount } from '../../types/experience';
import {
    findFeeById,
    formatCleaningFeeDetails,
} from './Fees/ExperienceChargesForm';

export function getHalfDayPrice(charge: any, charges: any[]): string {
    const halfDayCharge =
        charge.type === FeeType.PerDeskPerDay
            ? charges.find((c: any) => c.type === FeeType.PerDeskPerHalfDay)
            : charge.type === FeeType.PerOfficePerDay
              ? charges.find((c: any) => c.type === FeeType.PerOfficePerHalfDay)
              : charge.type === FeeType.PerMeetingRoomPerDay
                ? charges.find(
                      (c: any) => c.type === FeeType.PerMeetingRoomPerHalfDay,
                  )
                : charge.type === FeeType.EntireVenuePerDay
                  ? charges.find(
                        (c: any) => c.type === FeeType.EntireVenuePerHalfDay,
                    )
                  : null;
    return halfDayCharge
        ? ` ($${halfDayCharge.unitPrice.toFixed(2)} half day)`
        : '';
}

export function getPriceLabel(
    charge: any,
    experienceType?: string,
    experienceSubType?: string,
): string {
    return charge?.chargeType === 'price'
        ? charge.type === FeeType.PerDeskPerDay
            ? 'Desk/Day'
            : charge.type === FeeType.PerOfficePerDay
              ? 'Office/Day'
              : charge.type === FeeType.PerMeetingRoomPerDay
                ? 'Meeting Room/Day'
                : charge.type === FeeType.EntireVenuePerDay
                  ? 'Entire Venue/Day'
                  : charge.type === FeeType.EntireVenuePerNight
                    ? 'Entire Venue/Night'
                    : camelCaseToTitleCase(
                          charge?.type === FeeType.PerOfficePerDay &&
                              experienceType === 'commercial' &&
                              experienceSubType === 'house'
                              ? 'perRoomPerDay'
                              : charge?.type,
                      )
        : `${camelCaseToSentenceCase(charge?.name)} (${charge.type})`;
}

export function formatFlexibleDayPriceLine(charge: any): string {
    return `$${charge.dayPrice} - Per Day: ${formatTime(
        charge.dayCheckin,
    )} - ${formatTime(charge.dayCheckout)}`;
}

export function formatFlexibleNightPriceLine(charge: any): string {
    return `$${charge.nightPrice} - Per Night: ${formatTime(
        charge.nightCheckin,
    )} - ${formatTime(charge.nightCheckout)}`;
}

export function formatFlexibleHourPriceLine(charge: any): string {
    return `$${charge.hourPrice} - Per Hour (outside of standard check-in/checkout)`;
}

export function formatBaseUnitPriceLine(
    charge: any,
    charges: any[],
    experienceType?: string,
    experienceSubType?: string,
): string {
    return `$${charge.unitPrice.toFixed(2)} - ${getPriceLabel(
        charge,
        experienceType,
        experienceSubType,
    )}${getHalfDayPrice(charge, charges)}`;
}

const IndividualExperienceCharges = ({
    charges,
    experienceType,
    experienceSubType,
    discounts,
    excludedFromBilling,
    spaces,
    halfDayCheckinTime,
    halfDayCheckoutTime,
}: {
    charges: any;
    experienceType: any;
    experienceSubType: any;
    discounts: any;
    excludedFromBilling?: boolean;
    spaces: any[];
    halfDayCheckinTime?: string;
    halfDayCheckoutTime?: string;
}) => {
    const theme = useTheme();
    const [showPricingDetails, setShowPricingDetails] = useState(
        excludedFromBilling ? true : false,
    );
    const loggedInUser = UserRole();
    const { data: availableFees = [] } = useGetFeeListQuery(
        loggedInUser?.company,
    );
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isIpad = useMediaQuery(theme.breakpoints.down('md'));
    const hasHalfDayPrice = charges.some((charge: any) =>
        [
            FeeType.PerDeskPerHalfDay,
            FeeType.PerOfficePerHalfDay,
            FeeType.PerMeetingRoomPerHalfDay,
            FeeType.EntireVenuePerHalfDay,
        ].includes(charge.type),
    );
    const showHalfDayTimeNote =
        !!halfDayCheckinTime && !!halfDayCheckoutTime && hasHalfDayPrice;

    const activeDiscounts: ExperienceDiscount[] = discounts.filter(
        (discount: ExperienceDiscount) =>
            !discount.disabled && !discount.expired,
    );

    const additionalFees = charges?.filter(
        (charge: any) => charge.chargeType === 'fee',
    );

    const basePrices = charges?.filter(
        (charge: any) =>
            ['price', 'flexiblePrice'].includes(charge.chargeType) &&
            ![
                FeeType.PerDeskPerHalfDay,
                FeeType.PerOfficePerHalfDay,
                FeeType.PerMeetingRoomPerHalfDay,
                FeeType.EntireVenuePerHalfDay,
            ].includes(charge.type),
    );

    const chargeWithWeekendPrice = charges?.find(
        (charge: any) => charge.weekendPrice,
    );

    const cleaningFees = charges?.filter(
        (charge: any) => charge.chargeType === 'cleaningFee',
    );

    return (
        <Grid
            sx={{
                height: 'auto',
                paddingTop: isIpad ? '10px' : '30px',
                width: isMobile || isIpad ? '100%' : '88%',
            }}
        >
            <Divider
                sx={{
                    width: '100%',
                    color: theme.palette.faded.main,
                }}
            />
            <Grid>
                <Grid
                    sx={{
                        paddingTop: '30px',
                        paddingBottom: '10px',
                    }}
                >
                    <Stack>
                        <Typography variant='h2' sx={{ pb: 1 }} gutterBottom>
                            Pricing & Fees
                        </Typography>

                        {!excludedFromBilling && (
                            <Box>
                                <Typography variant='body1'>
                                    The amounts listed cover the cost of the
                                    experience, including any additional
                                    services.{' '}
                                    {showPricingDetails && (
                                        <span>
                                            Note: All amounts are excl GST.
                                        </span>
                                    )}
                                </Typography>

                                <Box
                                    display='flex'
                                    justifyContent='flex-end'
                                    mt={0.5}
                                >
                                    <Button
                                        color='primary'
                                        onClick={() =>
                                            setShowPricingDetails(
                                                !showPricingDetails,
                                            )
                                        }
                                        sx={{
                                            textTransform: 'none',
                                            fontFamily:
                                                theme.typography.fontFamily,
                                            fontSize: '.75rem',

                                            minWidth: 'auto',
                                        }}
                                    >
                                        {showPricingDetails
                                            ? 'Show Less'
                                            : 'Show More'}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </Grid>

                <Collapse in={showPricingDetails}>
                    {excludedFromBilling ? (
                        <Typography variant='body1'>
                            Please note that the host has opted to exclude you
                            from billing for this particular experience, and as
                            such, no charges will apply.
                        </Typography>
                    ) : (
                        <>
                            <Grid
                                sx={{
                                    minWidth: isMobile || isIpad ? 0 : '500px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '20px',
                                }}
                            >
                                {basePrices?.length > 0 && (
                                    <Typography variant='body1'>
                                        <Typography
                                            variant='body1'
                                            sx={{
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Base Price
                                        </Typography>
                                        <Typography
                                            component='ul'
                                            variant='body1'
                                        >
                                            {basePrices?.flatMap(
                                                (
                                                    charge: any,
                                                    index: number,
                                                ) => {
                                                    let priceItem;

                                                    if (
                                                        charge.chargeType ===
                                                        'flexiblePrice'
                                                    ) {
                                                        priceItem = (
                                                            <>
                                                                <li
                                                                    key={`${index}-day`}
                                                                >
                                                                    {formatFlexibleDayPriceLine(
                                                                        charge,
                                                                    )}
                                                                </li>
                                                                <li
                                                                    key={`${index}-night`}
                                                                >
                                                                    {formatFlexibleNightPriceLine(
                                                                        charge,
                                                                    )}
                                                                </li>
                                                                <li
                                                                    key={`${index}-hour`}
                                                                >
                                                                    {formatFlexibleHourPriceLine(
                                                                        charge,
                                                                    )}
                                                                </li>
                                                            </>
                                                        );
                                                    } else {
                                                        priceItem = (
                                                            <li
                                                                key={`${index}-regular`}
                                                            >
                                                                {` $${charge?.unitPrice.toFixed(
                                                                    2,
                                                                )} - ${getPriceLabel(
                                                                    charge,
                                                                    experienceType,
                                                                    experienceSubType,
                                                                )}`}{' '}
                                                                <span
                                                                    style={{
                                                                        fontSize:
                                                                            isMobile
                                                                                ? '14px'
                                                                                : '16px',
                                                                    }}
                                                                >
                                                                    {getHalfDayPrice(
                                                                        charge,
                                                                        charges,
                                                                    )}
                                                                </span>
                                                            </li>
                                                        );
                                                    }

                                                    return [priceItem];
                                                },
                                            )}
                                        </Typography>
                                        {showHalfDayTimeNote && (
                                            <Typography
                                                variant='body2'
                                                sx={{
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {experienceType ===
                                                ExperienceType.Commercial
                                                    ? `Half-day rates apply when check-in is before ${formatTime(
                                                          halfDayCheckoutTime!,
                                                      )} or check-out is after ${formatTime(
                                                          halfDayCheckinTime!,
                                                      )}.`
                                                    : experienceType ===
                                                            ExperienceType.Property &&
                                                        charges.find(
                                                            (charge: any) =>
                                                                charge.canChargeHalfDay,
                                                        )
                                                      ? `Hourly rates apply for half day bookings when check-out is before ${formatTime(
                                                            halfDayCheckoutTime!,
                                                        )} or check-in is after ${formatTime(
                                                            halfDayCheckinTime!,
                                                        )}. `
                                                      : null}
                                            </Typography>
                                        )}
                                    </Typography>
                                )}

                                {chargeWithWeekendPrice && (
                                    <Typography variant='body1'>
                                        <Typography
                                            variant='body1'
                                            sx={{
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Weekend Price
                                        </Typography>

                                        <Typography
                                            component='ul'
                                            variant='body1'
                                        >
                                            {chargeWithWeekendPrice.chargeType ===
                                            'flexiblePrice' ? (
                                                <>
                                                    <li>
                                                        {formatFlexibleDayPriceLine(
                                                            {
                                                                dayPrice:
                                                                    chargeWithWeekendPrice
                                                                        .weekendPrice
                                                                        .dayPrice,
                                                                dayCheckin:
                                                                    chargeWithWeekendPrice.dayCheckin,
                                                                dayCheckout:
                                                                    chargeWithWeekendPrice.dayCheckout,
                                                            },
                                                        )}
                                                    </li>
                                                    <li>
                                                        {formatFlexibleNightPriceLine(
                                                            {
                                                                nightPrice:
                                                                    chargeWithWeekendPrice
                                                                        .weekendPrice
                                                                        .nightPrice,
                                                                nightCheckin:
                                                                    chargeWithWeekendPrice.nightCheckin,
                                                                nightCheckout:
                                                                    chargeWithWeekendPrice.nightCheckout,
                                                            },
                                                        )}
                                                    </li>
                                                    <li>
                                                        {formatFlexibleHourPriceLine(
                                                            {
                                                                hourPrice:
                                                                    chargeWithWeekendPrice
                                                                        .weekendPrice
                                                                        .hourPrice,
                                                            },
                                                        )}
                                                    </li>
                                                </>
                                            ) : (
                                                <>
                                                    <li>
                                                        {` $${chargeWithWeekendPrice?.weekendPrice?.unitPrice.toFixed(
                                                            2,
                                                        )} - ${
                                                            chargeWithWeekendPrice?.chargeType ===
                                                            'price'
                                                                ? camelCaseToTitleCase(
                                                                      chargeWithWeekendPrice?.type,
                                                                  )
                                                                : `${camelCaseToSentenceCase(
                                                                      chargeWithWeekendPrice?.name,
                                                                  )} (${
                                                                      findFeeById(
                                                                          chargeWithWeekendPrice.feeId,
                                                                          availableFees,
                                                                      )?.type
                                                                  })`
                                                        } `}

                                                        {` (${chargeWithWeekendPrice?.weekendPrice?.days
                                                            .map(
                                                                (day: string) =>
                                                                    capitalizeName(
                                                                        day,
                                                                    ),
                                                            )
                                                            .join(', ')})`}
                                                    </li>
                                                </>
                                            )}
                                        </Typography>
                                    </Typography>
                                )}

                                {cleaningFees.length > 0 && (
                                    <Typography variant='body1'>
                                        <Typography
                                            variant='body1'
                                            sx={{
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Cleaning Fees
                                        </Typography>

                                        <Typography
                                            component='ul'
                                            variant='body1'
                                        >
                                            {cleaningFees.map(
                                                (fee: any, idx: number) => {
                                                    const details =
                                                        formatCleaningFeeDetails(
                                                            fee,
                                                            spaces,
                                                        ); // array of strings or structured parts
                                                    return details.map(
                                                        (
                                                            detail: string,
                                                            innerIdx: number,
                                                        ) => (
                                                            <li
                                                                key={`${idx}-${innerIdx}`}
                                                            >
                                                                {detail}
                                                            </li>
                                                        ),
                                                    );
                                                },
                                            )}
                                        </Typography>
                                    </Typography>
                                )}

                                {additionalFees.length > 0 && (
                                    <Typography variant='body1'>
                                        <Typography
                                            variant='body1'
                                            sx={{
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Additional Fees
                                        </Typography>

                                        <Typography
                                            component='ul'
                                            variant='body1'
                                        >
                                            {additionalFees.map(
                                                (
                                                    charge: any,
                                                    index: number,
                                                ) => (
                                                    <li
                                                        key={`${index}-regular`}
                                                    >
                                                        {` $${charge?.unitPrice.toFixed(
                                                            2,
                                                        )} - ${
                                                            charge?.chargeType ===
                                                            'price'
                                                                ? camelCaseToTitleCase(
                                                                      charge?.type ===
                                                                          'perOfficePerDay' &&
                                                                          experienceType ===
                                                                              'commercial' &&
                                                                          experienceSubType ===
                                                                              'house'
                                                                          ? 'perRoomPerDay'
                                                                          : charge?.type,
                                                                  )
                                                                : `${camelCaseToSentenceCase(
                                                                      charge?.name,
                                                                  )} (${
                                                                      charge.type
                                                                  })`
                                                        }`}
                                                    </li>
                                                ),
                                            )}
                                        </Typography>
                                    </Typography>
                                )}
                            </Grid>

                            {activeDiscounts.length > 0 && (
                                <Grid>
                                    <Grid
                                        sx={{
                                            paddingTop: '30px',
                                            paddingBottom: '10px',
                                        }}
                                    >
                                        <Typography
                                            variant='body1'
                                            sx={{
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            Discounts
                                        </Typography>
                                    </Grid>
                                    <Typography component='ul' variant='body1'>
                                        {activeDiscounts?.map(
                                            (discount, index) => (
                                                <li
                                                    key={index}
                                                    style={{ marginBottom: 10 }}
                                                >
                                                    <Typography
                                                        variant='body1'
                                                        fontWeight='bold'
                                                    >
                                                        {discount.name} -{' '}
                                                        {discount.percentage}%
                                                    </Typography>
                                                    <Box>
                                                        <Typography
                                                            variant='caption'
                                                            display='block'
                                                            sx={{
                                                                fontSize:
                                                                    '12px',
                                                            }}
                                                        >
                                                            Applicable Dates:
                                                        </Typography>
                                                        <Typography
                                                            variant='caption'
                                                            sx={{
                                                                fontSize:
                                                                    '12px',
                                                                fontStyle:
                                                                    'italic',
                                                            }}
                                                        >
                                                            <ul
                                                                style={{
                                                                    paddingLeft: 20,
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                {discount.dates.map(
                                                                    (
                                                                        date: any,
                                                                        dateIndex: number,
                                                                    ) => (
                                                                        <li
                                                                            key={
                                                                                dateIndex
                                                                            }
                                                                        >
                                                                            {new Date(
                                                                                date.startDate,
                                                                            ).toLocaleDateString(
                                                                                'en-AU',
                                                                            )}{' '}
                                                                            to{' '}
                                                                            {new Date(
                                                                                date.endDate,
                                                                            ).toLocaleDateString(
                                                                                'en-AU',
                                                                            )}
                                                                        </li>
                                                                    ),
                                                                )}
                                                            </ul>
                                                        </Typography>
                                                    </Box>
                                                </li>
                                            ),
                                        )}
                                    </Typography>
                                </Grid>
                            )}
                        </>
                    )}
                </Collapse>
            </Grid>
        </Grid>
    );
};

export default IndividualExperienceCharges;
